import { useState, useMemo, useCallback, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { positionColors, positionColorDotBg } from "@/mocks/positionsData";

const styleSheet = document.createElement("style");
styleSheet.textContent = `
@keyframes slideInRight {
  from { transform: translateX(100%); }
  to { transform: translateX(0); }
}
.animate-slide-in-right {
  animation: slideInRight 0.25s ease-out;
}
`;
if (!document.head.querySelector("[data-org-chart-styles]")) {
  styleSheet.setAttribute("data-org-chart-styles", "true");
  document.head.appendChild(styleSheet);
}

export interface Position {
  id: string;
  name: string;
  level: number;
  color: string;
  dataAccess: string;
}

export interface TeamMember {
  id: string;
  name: string;
  email: string;
  phone: string;
  positionId: string;
  teamName: string;
  branch: string | any;
  avatarInitials: string;
  leadCount: number;
  reportsToMemberId: string;
}

interface OrgChartProps {
  teamMembers: TeamMember[];
  positions: Position[];
  onEditMember?: (member: TeamMember) => void;
  onViewLeads?: (member: TeamMember) => void;
  onReassign?: (member: TeamMember) => void;
}

interface TreeNode {
  member: TeamMember;
  children: TreeNode[];
  position: Position | undefined;
}

function getDataAccessLabel2(pos: Position | undefined): string {
  if (!pos) return "Own Data Only";
  const map: Record<string, string> = {
    all: "All Company Data",
    region: "Department / Region Data",
    team: "Team Data Only",
    own_team: "Own + Team View",
    own: "Own Data Only",
    view: "View Only",
  };
  return map[pos.dataAccess] || "Own Data Only";
}

export default function OrgChart({
  teamMembers,
  positions,
  onEditMember,
  onViewLeads,
  onReassign,
}: OrgChartProps) {
  const navigate = useNavigate();
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const [fullyExpandedNodes, setFullyExpandedNodes] = useState<Set<string>>(new Set());
  const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [zoom, setZoom] = useState(1);
  const treeRef = useRef<HTMLDivElement>(null);

  // Build tree from flat list
  const tree = useMemo(() => {
    const memberMap = new Map<string, TeamMember>();
    teamMembers.forEach((m) => memberMap.set(m.id, m));

    const childrenMap = new Map<string, TeamMember[]>();
    teamMembers.forEach((m) => {
      if (m.reportsToMemberId) {
        const list = childrenMap.get(m.reportsToMemberId) || [];
        list.push(m);
        childrenMap.set(m.reportsToMemberId, list);
      }
    });

    function buildNode(member: TeamMember): TreeNode {
      const children = (childrenMap.get(member.id) || []).map(buildNode);
      const position = positions.find((p) => p?.id === member.positionId);
      return { member, children, position };
    }

    return teamMembers
      .filter((m) => !m.reportsToMemberId)
      .map(buildNode);
  }, [teamMembers, positions]);

  // Initialize default expand state: L1/L2 expanded, L3 collapsed
  useEffect(() => {
    const init = new Set<string>();
    function walk(node: TreeNode, depth: number) {
      const level = node.position?.level || 0;
      if (level <= 2 && node.children.length > 0) {
        init.add(node.member.id);
      }
      if (level <= 2) {
        node.children.forEach((c) => walk(c, depth + 1));
      }
    }
    tree.forEach((n) => walk(n, 0));
    setExpandedNodes(init);
  }, [tree]);

  const toggleExpand = useCallback((memberId: string) => {
    setExpandedNodes((prev) => {
      const next = new Set(prev);
      if (next.has(memberId)) next.delete(memberId);
      else next.add(memberId);
      return next;
    });
  }, []);

  const toggleFullExpand = useCallback((memberId: string) => {
    setFullyExpandedNodes((prev) => {
      const next = new Set(prev);
      if (next.has(memberId)) next.delete(memberId);
      else next.add(memberId);
      return next;
    });
  }, []);

  const getPosColorClass = (color: string) => positionColors[color] || "bg-gray-100 text-gray-700";
  const getPosDotClass = (color: string) => positionColorDotBg[color] || "bg-gray-400";

  // Search filtering
  const matchingIds = useMemo(() => {
    if (!searchQuery.trim()) return new Set<string>();
    const q = searchQuery.toLowerCase();
    const ids = new Set<string>();
    teamMembers.forEach((m) => {
      if (m.name.toLowerCase().includes(q) || m.email.toLowerCase().includes(q)) {
        ids.add(m.id);
      }
    });
    return ids;
  }, [searchQuery, teamMembers]);

  const hasSearch = searchQuery.trim().length > 0;

  // Direct reports count for a member
  const directReportCount = useCallback((memberId: string) => {
    return teamMembers.filter((m) => m.reportsToMemberId === memberId).length;
  }, [teamMembers]);

  // Zoom handlers
  const zoomIn = () => setZoom((z) => Math.min(z + 0.15, 2));
  const zoomOut = () => setZoom((z) => Math.max(z - 0.15, 0.4));
  const zoomFit = () => setZoom(1);

  // Render a single person card
  function PersonCard({ node, isHighlighted, isDimmed }: { node: TreeNode; isHighlighted: boolean; isDimmed: boolean }) {
    const { member, position } = node;
    const color = position?.color || "#64748b";
    const colorClass = getPosColorClass(color);
    const dotClass = getPosDotClass(color);
    const directReports = directReportCount(member.id);
    const hasChildren = node.children.length > 0;
    const isExpanded = expandedNodes.has(member.id);

    const branchDisplay = typeof member.branch === "object" && member.branch !== null
      ? member.branch.name
      : member.branch || "—";

    return (
      <div
        className={`bg-white rounded-xl border p-4 min-w-[180px] max-w-[220px] text-center relative cursor-pointer transition-all duration-200 flex-shrink-0 ${
          isHighlighted
            ? "border-2 border-indigo-500 bg-indigo-50 shadow-md"
            : isDimmed
            ? "opacity-20 border-gray-200"
            : "border-gray-200 hover:border-indigo-300 hover:shadow-md"
        }`}
        onClick={(e) => {
          e.stopPropagation();
          setSelectedMember(member);
        }}
      >
        {/* Position color dot */}
        <div className={`absolute top-3 right-3 w-2.5 h-2.5 rounded-full ${dotClass}`} />

        {/* Avatar */}
        <div className={`w-12 h-12 rounded-full mx-auto mb-2 flex items-center justify-center text-sm font-bold ${colorClass}`}>
          {member.avatarInitials}
        </div>

        {/* Name */}
        <p className="font-semibold text-gray-900 text-sm">{member.name}</p>

        {/* Position */}
        <p className="text-xs text-gray-500 mt-0.5">{position?.name || "—"}</p>

        {/* Branch */}
        <p className="text-[11px] text-gray-400">{branchDisplay}</p>

        {/* Stats pills */}
        <div className="mt-2 flex items-center justify-center gap-1 flex-wrap">
          <span className="bg-indigo-50 text-indigo-700 text-[10px] font-medium rounded-full px-2 py-0.5 whitespace-nowrap">
            {member.leadCount} {member.leadCount === 1 ? "lead" : "leads"}
          </span>
          <span className="bg-gray-100 text-gray-600 text-[10px] font-medium rounded-full px-2 py-0.5 whitespace-nowrap">
            &#x1F465; {directReports}
          </span>
        </div>

        {/* Expand/collapse button */}
        {hasChildren && (
          <div className="mt-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                toggleExpand(member.id);
              }}
              className="bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 rounded-full px-3 py-1 text-[11px] text-indigo-600 font-medium cursor-pointer transition-colors whitespace-nowrap"
            >
              {isExpanded ? "▲ Hide team" : `▼ Show team (${node.children.length})`}
            </button>
          </div>
        )}
      </div>
    );
  }

  // Render "+X MORE" card
  function MoreCard({ count, onClick }: { count: number; onClick: (e: React.MouseEvent) => void }) {
    return (
      <div
        className="bg-gray-50 border-2 border-dashed border-gray-200 rounded-xl p-4 min-w-[140px] text-center cursor-pointer hover:border-indigo-300 hover:bg-indigo-50 transition-all flex-shrink-0"
        onClick={onClick}
      >
        <p className="font-bold text-2xl text-gray-500">+{count}</p>
        <p className="text-[11px] text-gray-400">more members</p>
        <p className="text-[11px] text-indigo-500 mt-1">Click to expand</p>
      </div>
    );
  }

  // Render connecting lines and children
  function ChildrenContainer({ node, children, maxVisible }: { node: TreeNode; children: TreeNode[]; maxVisible: number }) {
    const isExpanded = expandedNodes.has(node.member.id);
    const isFullyExpanded = fullyExpandedNodes.has(node.member.id);
    const visible = isFullyExpanded ? children : children.slice(0, maxVisible);
    const hidden = isFullyExpanded ? 0 : Math.max(0, children.length - maxVisible);

    if (!isExpanded) return null;

    return (
      <div className="flex flex-col items-center">
        <div className="w-0.5 h-6 bg-gray-200" />
        <div className="relative flex flex-col items-center">
          {children.length > 1 && (
            <div className="absolute top-0 left-0 right-0 h-0.5 bg-gray-200" style={{ width: "100%" }} />
          )}
          <div className="flex items-start justify-center gap-4 pt-6">
            {visible.map((child) => (
              <div key={child.member.id} className="relative flex flex-col items-center">
                <div className="w-0.5 h-6 bg-gray-200 absolute -top-6 left-1/2 -translate-x-1/2" />
                <TreeNode node={child} depth={0} />
              </div>
            ))}
            {hidden > 0 && (
              <div className="relative flex flex-col items-center">
                <div className="w-0.5 h-6 bg-gray-200 absolute -top-6 left-1/2 -translate-x-1/2" />
                <MoreCard count={hidden} onClick={(e) => { e.stopPropagation(); toggleFullExpand(node.member.id); }} />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Recursive tree node
  function TreeNode({ node, depth }: { node: TreeNode; depth: number }) {
    const isHighlighted = hasSearch && matchingIds.has(node.member.id);
    const isDimmed = hasSearch && !matchingIds.has(node.member.id) && matchingIds.size > 0;

    return (
      <div className="flex flex-col items-center">
        <PersonCard node={node} isHighlighted={isHighlighted} isDimmed={isDimmed} />
        {node.children.length > 0 && (
          <ChildrenContainer node={node} children={node.children} maxVisible={3} />
        )}
      </div>
    );
  }

  // Selected member detail panel
  function DetailPanel() {
    if (!selectedMember) return null;
    const pos = positions.find((p) => p.id === selectedMember.positionId);
    const color = pos?.color || "#64748b";
    const colorClass = getPosColorClass(color);
    const directReports = teamMembers.filter((m) => m.reportsToMemberId === selectedMember.id);
    const totalTeam = countSubtree(selectedMember.id) - 1;
    const manager = teamMembers.find((m) => m.id === selectedMember.reportsToMemberId);

    function countSubtree(memberId: string): number {
      let count = 1;
      teamMembers.filter((m) => m.reportsToMemberId === memberId).forEach((c) => {
        count += countSubtree(c.id);
      });
      return count;
    }

    const branchDisplay = typeof selectedMember.branch === "object" && selectedMember.branch !== null
      ? selectedMember.branch.name
      : selectedMember.branch || "—";

    return (
      <>
        <div className="fixed inset-0 z-30" onClick={() => setSelectedMember(null)} />
        <div className="fixed top-0 right-0 h-full w-[300px] bg-white border-l border-gray-200 shadow-2xl z-40 overflow-y-auto animate-slide-in-right">
          <button
            onClick={() => setSelectedMember(null)}
            className="absolute top-4 right-4 p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-lg w-5 h-5 flex items-center justify-center" />
          </button>

          <div className="p-5 pt-12 space-y-5">
            <div className="text-center">
              <div className={`w-16 h-16 rounded-full mx-auto mb-3 flex items-center justify-center text-xl font-bold ${colorClass}`}>
                {selectedMember.avatarInitials}
              </div>
              <h3 className="font-bold text-xl text-gray-900">{selectedMember.name}</h3>
              {pos && (
                <span className={`inline-block mt-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold ${colorClass}`}>
                  {pos.name}
                </span>
              )}
              <p className="text-sm text-gray-500 mt-1">{branchDisplay}</p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="bg-gray-50 rounded-lg p-3 text-center">
                <p className="text-lg font-bold text-gray-900">{selectedMember.leadCount}</p>
                <p className="text-[11px] text-gray-500">Own Leads</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3 text-center">
                <p className="text-lg font-bold text-gray-900">{directReports.length}</p>
                <p className="text-[11px] text-gray-500">Direct Reports</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3 text-center">
                <p className="text-lg font-bold text-gray-900">{totalTeam}</p>
                <p className="text-[11px] text-gray-500">Total Team</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3 text-center">
                <p className="text-xs font-semibold text-gray-700">{getDataAccessLabel2(pos)}</p>
                <p className="text-[11px] text-gray-500">Data Access</p>
              </div>
            </div>

            <div className="space-y-2 text-sm text-gray-600">
              <p className="flex items-center gap-2"><i className="ri-mail-line text-gray-400" />{selectedMember.email}</p>
              <p className="flex items-center gap-2"><i className="ri-phone-line text-gray-400" />{selectedMember.phone}</p>
            </div>

            {manager && (
              <div>
                <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Reports to</p>
                <div className="flex items-center gap-2.5 bg-gray-50 rounded-lg p-2.5">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-[10px] font-bold ${getPosColorClass(positions.find((p) => p.id === manager.positionId)?.color || "#64748b")}`}>
                    {manager.avatarInitials}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{manager.name}</p>
                    <p className="text-[11px] text-gray-500">{positions.find((p) => p.id === manager.positionId)?.name || "—"}</p>
                  </div>
                </div>
              </div>
            )}

            {directReports.length > 0 && (
              <div>
                <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Direct reports ({directReports.length})</p>
                <div className="flex items-center gap-1.5 flex-wrap">
                  {directReports.slice(0, 4).map((dr) => {
                    const dc = positions.find((p) => p.id === dr.positionId)?.color || "#64748b";
                    return (
                      <button
                        key={dr.id}
                        onClick={() => setSelectedMember(dr)}
                        className={`w-8 h-8 rounded-full flex items-center justify-center text-[10px] font-bold cursor-pointer hover:ring-2 hover:ring-indigo-400 transition-all ${getPosColorClass(dc)}`}
                        title={dr.name}
                      >
                        {dr.avatarInitials}
                      </button>
                    );
                  })}
                  {directReports.length > 4 && (
                    <span className="text-[11px] text-gray-400">+{directReports.length - 4} more</span>
                  )}
                </div>
              </div>
            )}

            <div className="space-y-2 pt-2">
              <button
                onClick={() => onEditMember ? onEditMember(selectedMember) : navigate(`/settings/users`)}
                className="w-full px-4 py-2.5 rounded-lg border border-indigo-600 text-indigo-600 text-sm font-medium hover:bg-indigo-50 transition-colors whitespace-nowrap cursor-pointer"
              >
                Edit Profile
              </button>
              <button
                onClick={() => {
                  if (onViewLeads) {
                    onViewLeads(selectedMember);
                  } else {
                    navigate(`/leads?agent=${encodeURIComponent(selectedMember.name)}`);
                  }
                }}
                className="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-gray-700 text-sm font-medium hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
              >
                View Their Leads
              </button>
              <button
                onClick={() => onReassign && onReassign(selectedMember)}
                className="w-full px-4 py-2.5 rounded-lg border border-amber-500 text-amber-600 text-sm font-medium hover:bg-amber-50 transition-colors whitespace-nowrap cursor-pointer"
              >
                Reassign
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  const legendLevels = [
    { color: "#8b5cf6", label: "Level 1 (Owner)", dotClass: "bg-purple-500" },
    { color: "#4f46e5", label: "Level 2 (Director)", dotClass: "bg-indigo-500" },
    { color: "#3b82f6", label: "Level 3 (Manager)", dotClass: "bg-blue-500" },
    { color: "#14b8a6", label: "Level 4 (Team Lead)", dotClass: "bg-teal-500" },
    { color: "#22c55e", label: "Level 5 (Agent)", dotClass: "bg-green-500" },
  ];

  return (
    <div className="relative">
      <div className="mb-4">
        <div className="relative w-64">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm w-4 h-4 flex items-center justify-center" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search team member..."
            className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-gray-200 bg-white text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 transition-all"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer"
            >
              <i className="ri-close-line text-sm w-4 h-4 flex items-center justify-center" />
            </button>
          )}
        </div>
      </div>

      {/* Org chart canvas */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 overflow-x-auto min-h-[500px] relative">
        <div className="absolute top-4 right-4 z-10 flex items-center bg-white rounded-lg border border-gray-200 shadow-sm p-1 gap-1">
          <button onClick={zoomIn} className="w-8 h-8 rounded hover:bg-gray-100 flex items-center justify-center text-gray-600 text-sm cursor-pointer" title="Zoom in">
            <i className="ri-zoom-in-line" />
          </button>
          <button onClick={zoomOut} className="w-8 h-8 rounded hover:bg-gray-100 flex items-center justify-center text-gray-600 text-sm cursor-pointer" title="Zoom out">
            <i className="ri-zoom-out-line" />
          </button>
          <button onClick={zoomFit} className="w-8 h-8 rounded hover:bg-gray-100 flex items-center justify-center text-gray-600 text-xs font-medium cursor-pointer" title="Reset zoom">
            100%
          </button>
        </div>

        <div
          ref={treeRef}
          style={{ transform: `scale(${zoom})`, transformOrigin: "top center", transition: "transform 0.2s ease-out" }}
          className="flex justify-center pt-8 pb-16 min-w-max"
        >
          {tree.length === 0 ? (
            <div className="text-center py-16 text-gray-400">
              <i className="ri-organization-chart text-4xl mb-2 block" />
              <p>No team members found</p>
            </div>
          ) : (
            <div className="flex items-start justify-center gap-8">
              {tree.map((rootNode) => (
                <TreeNode key={rootNode.member.id} node={rootNode} depth={0} />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Legend */}
      <div className="mt-4 bg-white rounded-xl border border-gray-200 p-4">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Levels</p>
        <div className="flex items-center gap-4 flex-wrap">
          {legendLevels.map((lvl) => (
            <div key={lvl.label} className="flex items-center gap-2">
              <span className={`w-2.5 h-2.5 rounded-full ${lvl.dotClass}`} />
              <span className="text-xs text-gray-600">{lvl.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Member detail drawer */}
      <DetailPanel />
    </div>
  );
}